import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Package, ShoppingCart, Users, DollarSign } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading) {
      if (!user || !user.is_admin) {
        navigate('/');
      } else {
        fetchStats();
      }
    }
  }, [user, authLoading]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12" data-testid="admin-dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold" data-testid="admin-dashboard-title">Admin Dashboard</h1>
          <p className="text-gray-600 mt-2">Manage your dropshipping store</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[
            { id: 'products', icon: Package, label: 'Total Products', value: stats?.total_products || 0, color: '#FF6B35', bg: 'rgba(255, 107, 53, 0.15)' },
            { id: 'orders', icon: ShoppingCart, label: 'Total Orders', value: stats?.total_orders || 0, color: '#00D9FF', bg: 'rgba(0, 217, 255, 0.15)' },
            { id: 'users', icon: Users, label: 'Total Users', value: stats?.total_users || 0, color: '#FFB800', bg: 'rgba(255, 184, 0, 0.15)' },
            { id: 'revenue', icon: DollarSign, label: 'Revenue', value: `$${stats?.total_revenue?.toFixed(2) || '0.00'}`, color: '#10B981', bg: 'rgba(16, 185, 129, 0.15)' }
          ].map((stat) => (
            <Card key={stat.id} className="p-6 border-0 shadow-md" style={{ background: stat.bg }} data-testid={`stat-${stat.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-2">{stat.label}</p>
                  <p className="text-3xl font-bold" style={{ color: stat.color }}>{stat.value}</p>
                </div>
                <div className="p-3 rounded-full" style={{ background: stat.color + '30' }}>
                  <stat.icon className="h-8 w-8" style={{ color: stat.color }} />
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card className="p-8 border-0 shadow-md hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-2xl font-bold mb-2">Products</h3>
                <p className="text-gray-600">Manage your product catalog</p>
              </div>
              <Package className="h-12 w-12 text-[#FF6B35]" />
            </div>
            <Link to="/admin/products">
              <Button className="w-full bg-[#FF6B35] hover:bg-[#ff8c5a] text-white rounded-full py-6 mt-4" data-testid="manage-products-button">
                Manage Products
              </Button>
            </Link>
          </Card>

          <Card className="p-8 border-0 shadow-md hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-2xl font-bold mb-2">Orders</h3>
                <p className="text-gray-600">View and manage customer orders</p>
              </div>
              <ShoppingCart className="h-12 w-12 text-[#00D9FF]" />
            </div>
            <Link to="/admin/orders">
              <Button className="w-full bg-[#00D9FF] hover:bg-[#00b3d9] text-white rounded-full py-6 mt-4" data-testid="manage-orders-button">
                Manage Orders
              </Button>
            </Link>
          </Card>

          <Card className="p-8 border-0 shadow-md hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-2xl font-bold mb-2">Sales Analytics</h3>
                <p className="text-gray-600">View sales insights & metrics</p>
              </div>
              <DollarSign className="h-12 w-12 text-[#10B981]" />
            </div>
            <Link to="/admin/analytics">
              <Button className="w-full bg-[#10B981] hover:bg-[#0d9668] text-white rounded-full py-6 mt-4" data-testid="view-analytics-button">
                View Analytics
              </Button>
            </Link>
          </Card>
        </div>
      </div>
    </div>
  );
}
